import { useState, useEffect } from 'react';
import { Key, Save, X, Search, Edit3, Trash2, Eye } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../services/api';

interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  creditsRemaining: number;
  isAdmin: boolean;
  scrapeCount: number;
  emailsFetched: number;
  createdAt: string;
}

export function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Edit state
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editCredits, setEditCredits] = useState<number>(0);
  
  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');


      const usersRes = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const usersData = await usersRes.json();
      if (usersData.success) setUsers(usersData.data);

    } catch (error) {
      console.error('Failed to fetch admin users', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCredits = async (userId: string) => {
    try {
      const token = localStorage.getItem('token');

      
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/credits`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ credits: editCredits })
      });
      
      const data = await res.json();
      if (data.success) {
        setUsers(users.map(u => u.id === userId ? { ...u, creditsRemaining: editCredits } : u));
        setEditingUserId(null);
      }
    } catch (error) {
      console.error('Failed to update credits', error);
    }
  };

  const toggleAdminStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token');

      
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/admin`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isAdmin: !currentStatus })
      });
      
      const data = await res.json();
      if (data.success) {
        setUsers(users.map(u => u.id === userId ? { ...u, isAdmin: !currentStatus } : u));
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Failed to update admin status', error);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (userId === currentUser?.id) {
       alert("You cannot delete yourself.");
       return;
    }
    
    if (window.confirm(`Are you absolutely sure you want to delete ${userEmail}? This will erase all their data and cannot be undone.`)) {
      try {
        const token = localStorage.getItem('token');

        
        const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
          method: 'DELETE',
          headers: { 
            'Authorization': `Bearer ${token}`
          }
        });
        
        const data = await res.json();
        if (data.success) {
          setUsers(users.filter(u => u.id !== userId));
        } else {
          alert(data.message || 'Failed to delete user.');
        }
      } catch (error) {
        console.error('Failed to delete user', error);
      }
    }
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(search.toLowerCase()) || 
    u.firstName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
            User Management
          </h1>
          <p className="text-gray-400 mt-2">Manage credits, roles, and accounts</p>
        </div>
      </div>

      <div className="bg-[#111111] rounded-2xl border border-white/10 overflow-hidden">
        <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search users by name or email..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-black/50 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-purple-500/50 w-full"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-400">
            <thead className="text-xs uppercase bg-black/20 text-gray-500">
              <tr>
                <th className="px-6 py-4 font-medium">User</th>
                <th className="px-6 py-4 font-medium">Credits</th>
                <th className="px-6 py-4 font-medium">Scrapes History</th>
                <th className="px-6 py-4 font-medium">Joined</th>
                <th className="px-6 py-4 font-medium">Role</th>
                <th className="px-6 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {isLoading && (
                  <tr><td colSpan={6} className="px-6 py-8 text-center">Loading users...</td></tr>
              )}
              {!isLoading && filteredUsers.map((u) => (
                <tr key={u.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-white">{u.firstName} {u.lastName}</div>
                    <div className="text-xs mt-1 text-gray-500">{u.email}</div>
                  </td>
                  
                  <td className="px-6 py-4">
                    {editingUserId === u.id ? (
                      <div className="flex items-center gap-2">
                        <input 
                          type="number"
                          className="bg-black border border-white/20 rounded px-2 py-1 w-20 text-white text-sm"
                          value={editCredits}
                          onChange={e => setEditCredits(parseInt(e.target.value) || 0)}
                        />
                        <button onClick={() => handleSaveCredits(u.id)} className="text-green-500 hover:text-green-400">
                          <Save className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingUserId(null)} className="text-red-500 hover:text-red-400">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className={`font-mono ${u.isAdmin ? 'text-purple-400 font-bold' : u.creditsRemaining < 10 ? 'text-red-400' : 'text-green-400'}`}>
                          {u.isAdmin ? 'Unlimited' : u.creditsRemaining}
                        </span>
                        {!u.isAdmin && (
                          <button 
                            onClick={() => { setEditingUserId(u.id); setEditCredits(u.creditsRemaining); }}
                            className="text-gray-500 hover:text-white transition-colors cursor-pointer"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-white">{u.scrapeCount} jobs</div>
                    <div className="text-xs text-gray-500">{u.emailsFetched.toLocaleString()} emails</div>
                  </td>
                  <td className="px-6 py-4">{new Date(u.createdAt).toLocaleDateString()}</td>
                  
                  <td className="px-6 py-4">
                    <button 
                      onClick={() => toggleAdminStatus(u.id, u.isAdmin)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1.5 transition-colors cursor-pointer ${
                        u.isAdmin 
                          ? 'border-purple-500/30 text-purple-400 bg-purple-500/10 hover:bg-purple-500/20' 
                          : 'border-white/10 text-gray-400 bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <Key className="w-3 h-3" />
                      {u.isAdmin ? 'Admin' : 'User'}
                    </button>
                  </td>

                  <td className="px-6 py-4 text-right flex items-center justify-end gap-1">
                    <button 
                      onClick={() => navigate(`/admin/users/${u.id}/view`)}
                      className="p-2 text-gray-500 hover:text-purple-400 transition-colors cursor-pointer rounded-lg hover:bg-white/5"
                      title="View Full Profile"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteUser(u.id, u.email)}
                      className="p-2 text-gray-500 hover:text-red-400 transition-colors cursor-pointer rounded-lg hover:bg-white/5"
                      title="Delete User"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {!isLoading && filteredUsers.length === 0 && (
            <div className="p-12 text-center text-gray-500">
              No users found matching "{search}"
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
